import { isFundingDevelopmentBadge } from '../../../shared/utils/development-badge.util';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { ProjectsService } from '../../../core/services/projects.service';
import { PropertiesService } from '../../../core/services/properties.service';
import { Project, Phase } from '../../../core/models/project.model';
import { Property } from '../../../core/models/property.model';
import { PropertyCardComponent } from '../../../shared/components/property-card/property-card.component';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';
import { CountrySelectorComponent } from '../../../shared/components/country-selector/country-selector.component';
import { CurrencyService } from '../../../core/services/currency.service';
import { NotificationService } from '../../../services/notification.service';
import { propertyStatusKey } from '../../../shared/utils/property-status.util';
import { ConfirmDialogService } from '../../../shared/components/confirm-dialog/confirm-dialog.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { PropertyUploadService } from '../../../core/services/property-upload.service';
import { ExternalViewerDialogService } from '../../../shared/components/external-viewer-dialog/external-viewer-dialog.service';
import { Store } from '@ngrx/store';
import { selectActor, selectCurrentUser } from '../../../store/auth/auth.selectors';
import { hasPropertyCreatePermission, permissionSetFromRaw } from '../../../shared/utils/permissions.util';
import { propertyFeatureLabels, propertyFeatureSearchTerms, propertyHasGarden, propertyHasTerrace } from '../../../shared/utils/property-features.util';
import { MediaLibraryWorkspace, openMediaLibraryPopup } from '../../../shared/utils/media-library-popup.util';

type SortKey = 'availability' | 'recent' | 'price-low' | 'price-high' | 'ppsqm-low' | 'ppsqm-high' | 'bedrooms' | 'bathrooms' | 'title' | 'unit-type';

@Component({
    selector: 'app-developer-project-detail',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, LucideIconsModule, PropertyCardComponent, LoadingSpinnerComponent, CountrySelectorComponent],
    templateUrl: './developer-project-detail.component.html',
    styleUrls: ['./developer-project-detail.component.scss'],
})
export class DeveloperProjectDetailComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private projectsService = inject(ProjectsService);
    private propertiesService = inject(PropertiesService);
    private currencyService = inject(CurrencyService);
    private notifications = inject(NotificationService);
    private viewerDialog = inject(ExternalViewerDialogService);
    private confirm = inject(ConfirmDialogService);
    private uploadService = inject(PropertyUploadService);
    private store = inject(Store);

    @ViewChild('projectEditForm') private projectEditForm?: ElementRef<HTMLElement>;

    projectId = '';
    project: Project | null = null;
    properties: Property[] = [];
    filteredProperties: Property[] = [];
    propertiesTotalCount = 0;
    propertiesPage = 1;
    propertiesHasNext = true;
    phases: Phase[] = [];
    phasesPage = 1;
    phasesHasNext = true;

    loadingProject = true;
	    loadingProperties = true;
	    loadingPhases = true;
	    savingProject = false;
		    deletingProject = false;
		    approvalUpdating = false;
		    approvalWithPropertiesUpdating = false;
		    draftingProperties = false;
		    verifying = false;
	    phaseSaving = false;
	    phaseSavingId: string | null = null;

	    editMode = false;
	    editName = '';
	    editDescription = '';
	    developmentBadgeMode: string = 'off_plan';
	    developmentBadgeCustom: string = '';
        projectReservationsEnabled = false;
        projectInvestorRequestOffersEnabled = false;
        projectPartnerRequestOffersEnabled = false;
        reservationsFeatureAvailable = false;
	    projectErrorMessage: string | null = null;

    phaseFormVisible = false;
    phaseName = '';
    phaseDeliveryDate = '';
    phaseStatus = 'Active';
    editingPhaseId: string | null = null;
    phaseError: string | null = null;

			    error: string | null = null;
			    projectLocationAddress = '';
			    editProjectLocationLink = '';
			    projectCountry = '';
	    vendorLogoFailed = false;
	    descriptionExpanded = false;
    projectImageUrl: string | null = null;
    projectImageError = false;
    projectImageErrorMessage: string | null = null;
    uploadingProjectImage = false;
    projectImageVersion = '';
    projectBrochureUrl: string | null = null;
	    projectMasterPlanUrl: string | null = null;
        projectLocationMapUrl: string | null = null;
        projectOfferCoverUrl: string | null = null;
	    uploadingProjectDocs = false;
	    projectDocsError: string | null = null;
	    isAdminContext = false;
	    private currencySub: Subscription | null = null;
    private userSub: Subscription | null = null;
    private permissionCodes: string[] = [];
    private currentUserOrgId: string | null = null;
    private currentUserId: string | null = null;
    private readonly mediaAssignedListener = (event: Event): void => {
        const detail = (event as CustomEvent<any>)?.detail;
        if (!detail || String(detail.target_type || '').trim().toLowerCase() !== 'project') return;
        if (String(detail.target_id || '').trim() !== this.projectId) return;
        this.loadProject();
    };

    availableCountries: string[] = [];
    availableTags: string[] = [];
    availableUnitTypes: string[] = [];
    bedroomOptions = [1, 2, 3, 4, 5, 6];
    bathroomOptions = [1, 2, 3, 4, 5, 6];
    showFilters = false;
    activeTab: 'properties' | 'phases' = 'properties';
    private countrySet = new Set<string>();
    private tagSet = new Set<string>();
    private unitTypeSet = new Set<string>();
    filters: {
        minPrice?: number | null;
        maxPrice?: number | null;
        bedrooms?: number | null;
        bathrooms?: number | null;
        location?: string | null;
        status: string;
        country: string;
        unitType: string;
        featureGarden: boolean;
        featureTerrace: boolean;
        featureGroundFloor: boolean;
        search: string;
        sortBy: SortKey;
        tags: string[];
    } = {
        status: 'any',
        country: 'any',
        unitType: 'any',
        featureGarden: false,
        featureTerrace: false,
        featureGroundFloor: false,
        search: '',
        sortBy: 'availability',
        tags: [],
        bedrooms: null,
        bathrooms: null,
        minPrice: null,
        maxPrice: null,
        location: null
    };

    get backLink(): any[] {
        if (this.isAdminContext && this.projectId) return ['/admin/projects/view', this.projectId];
        return ['/developer/projects'];
    }

    get canCreateProperties(): boolean {
        if (!hasPropertyCreatePermission(permissionSetFromRaw(this.permissionCodes))) return false;
        // Units can only be added to the actor's OWN org project. A project visible
        // via an incoming partner grant (different org) is view-only — the backend
        // scopes POST /properties to the actor's org and 404s cross-org, so hide the
        // Add Unit button here too. If we can't resolve either org yet, be safe and hide.
        if (!this.project) return false;
        const projectOrg = this.project.organizationId ? String(this.project.organizationId) : null;
        if (!projectOrg || !this.currentUserOrgId) return false;
        return projectOrg === this.currentUserOrgId;
    }

    // Edit/Delete a project is allowed when the actor is the project's own
    // creator/owner, OR holds projects:write — AND the project belongs to their
    // own org. A project visible purely via an incoming partner grant (different
    // org) is view-only — matching the backend, where mutate routes are
    // org-scoped (404 cross-org) and accept owner OR projects:write. A creator
    // can always manage the project they made, even without projects:write.
    get canManageProject(): boolean {
        // While the project is still loading we don't yet know its org/owner; hide
        // the controls until we can confirm it is own-org (avoids a flash).
        if (!this.project) return false;
        const projectOrg = this.project.organizationId ? String(this.project.organizationId) : null;
        // App-admin context is handled by its own admin buttons; in developer
        // context require an org match. If we can't resolve either org, be safe
        // and hide (the backend would 404 anyway).
        if (!projectOrg || !this.currentUserOrgId) return false;
        if (projectOrg !== this.currentUserOrgId) return false;
        // Owner can always manage their own project.
        const ownerId = this.project.ownerUserId ? String(this.project.ownerUserId) : null;
        const creatorId = this.project.createdByUserId ? String(this.project.createdByUserId) : null;
        if (this.currentUserId && (ownerId === this.currentUserId || creatorId === this.currentUserId)) {
            return true;
        }
        // Otherwise require the projects:write permission.
        const perms = permissionSetFromRaw(this.permissionCodes);
        return perms.has('projects:write');
    }

	    ngOnInit(): void {
        // Draft/Unpublished lifecycle badges are for the super admin only
        // (publishing console); everyone else works the project the same way
        // regardless of those states, so the pill only raises questions.
        this.store
            .select(selectActor)
            .subscribe((actor: any) => {
                this.isSuperAdmin = (actor?.role_code ?? actor?.roleCode) === 'app_admin'
                    || (actor?.actor_type ?? actor?.actorType ?? actor?.type) === 'app_admin';
            });
	        this.isAdminContext = (this.router.url || '').startsWith('/admin');
            if (typeof window !== 'undefined') {
                window.addEventListener('nibni:media-library-assigned', this.mediaAssignedListener as EventListener);
            }
	        this.currencySub = this.currencyService.currencyChanges$.subscribe(() => this.applyLocalFilters(this.properties));
            this.userSub = this.store.select(selectCurrentUser).subscribe((user: any) => {
                const raw = user?.permission_codes ?? user?.permissionCodes ?? [];
                this.permissionCodes = Array.isArray(raw) ? raw.map((value: any) => String(value || '').trim()) : [];
                this.currentUserOrgId = user?.org_id ? String(user.org_id) : (user?.orgId ? String(user.orgId) : null);
                this.currentUserId = user?.user_id ? String(user.user_id) : (user?.id ? String(user.id) : (user?.userId ? String(user.userId) : null));
                this.reservationsFeatureAvailable = Boolean(user?.feature_flags?.reservations_enabled);
            });
	        this.route.paramMap.subscribe((params) => {
	            this.projectId = params.get('id') || '';
	            if (this.projectId === 'new') {
	                this.navigateToProjectsHome();
	                return;
	            }
	            if (this.projectId) {
	                this.propertiesPage = 1;
	                this.phasesPage = 1;
	                this.loadProject();
	                this.loadPhases(false);
	                this.loadProperties(false);
	            }
	        });
	    }

	    ngOnDestroy(): void {
	        this.currencySub?.unsubscribe();
            this.userSub?.unsubscribe();
            if (typeof window !== 'undefined') {
                window.removeEventListener('nibni:media-library-assigned', this.mediaAssignedListener as EventListener);
            }
	    }

    private mediaWorkspace(): MediaLibraryWorkspace {
        return this.isAdminContext ? 'admin' : 'developer';
    }

    openProjectMediaPicker(
        slotKey: 'cover' | 'brochure' | 'master_plan' | 'location_map' | 'offer_cover',
        tab: 'view' | 'upload' = 'view'
    ): void {
        if (!this.projectId) return;
        openMediaLibraryPopup(this.mediaWorkspace(), {
            tab,
            targetType: 'project',
            slotKey,
            targetId: this.projectId,
            targetLabel: this.project?.name || this.editName || 'Current project',
            visibility: 'public'
        });
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }

    toggleTag(tag: string) {
        if (this.filters.tags.includes(tag)) {
            this.filters.tags = this.filters.tags.filter(t => t !== tag);
        } else {
            this.filters.tags = [...this.filters.tags, tag];
        }
        this.onFiltersChange();
    }

	    onFiltersChange() {
	        this.propertiesPage = 1;
	        this.loadProperties(false);
	    }

	    reloadProperties() {
	        this.propertiesPage = 1;
	        this.loadProperties(false);
	    }

	    reloadPhases() {
	        this.phasesPage = 1;
	        this.loadPhases(false);
	    }

	    loadMoreProperties() {
	        if (!this.propertiesHasNext || this.loadingProperties || !this.projectId) return;
	        this.propertiesPage += 1;
	        this.loadProperties(true);
	    }

	    loadMorePhases() {
	        if (!this.phasesHasNext || this.loadingPhases || !this.projectId) return;
	        this.phasesPage += 1;
	        this.loadPhases(true);
	    }

    hasActiveFilters(): boolean {
        const f = this.filters;
        return !!(
            (f.search && f.search.trim())
            || (f.location && f.location.trim())
            || (f.minPrice ?? null) !== null
            || (f.maxPrice ?? null) !== null
            || (f.bedrooms ?? null) !== null
            || (f.bathrooms ?? null) !== null
            || f.status !== 'any'
            || f.country !== 'any'
            || f.unitType !== 'any'
            || f.featureGarden
            || f.featureTerrace
            || f.featureGroundFloor
            || (f.tags && f.tags.length > 0)
        );
    }

    propertiesResultsSummary(): string {
        if (this.hasActiveFilters()) {
            return `Showing ${this.filteredProperties.length} of ${this.properties.length} loaded properties`;
        }
        const visibleTotal = this.propertiesHasNext
            ? Math.max(this.propertiesTotalCount, this.properties.length, this.filteredProperties.length)
            : Math.max(this.properties.length, this.filteredProperties.length);
        return `Showing ${this.filteredProperties.length} of ${visibleTotal} properties`;
    }

	    loadProject() {
	        if (!this.projectId) return;
	        this.loadingProject = true;
	        this.projectErrorMessage = null;
	        this.projectImageVersion = '';
	        this.projectDocsError = null;
	        this.projectsService.getProjectAuth(this.projectId).subscribe({
			            next: (project) => {
			                this.project = project;
			                this.editName = project?.name || '';
			                this.editDescription = project?.description || '';
                                this.syncDevelopmentBadgeFromProject(project);
                                this.projectReservationsEnabled = Boolean(project?.reservationsEnabled);
                                this.projectInvestorRequestOffersEnabled = Boolean(project?.investorRequestOffersEnabled);
                                this.projectPartnerRequestOffersEnabled = Boolean(project?.partnerRequestOffersEnabled);
                                this.projectLocationAddress = project?.locationAddress || '';
	                this.editProjectLocationLink = project?.locationLink || '';
	                this.projectCountry = project?.countryCode || project?.country || '';
		        this.refreshProjectImageUrl(project);
		        this.projectBrochureUrl = this.resolveProjectDocUrl(project, 'brochure');
		        this.projectMasterPlanUrl = this.resolveProjectDocUrl(project, 'masterPlan');
                this.projectLocationMapUrl = this.resolveProjectDocUrl(project, 'locationMap');
                this.projectOfferCoverUrl = this.resolveProjectOfferCoverUrl(project);
                this.projectImageError = false;
                this.projectImageErrorMessage = null;
                this.vendorLogoFailed = false;
                this.loadingProject = false;
                this.error = null;
            },
            error: (err) => {
                this.loadingProject = false;
                this.error = err?.message || 'Failed to load project';
            }
        });
    }

	    loadProperties(append = false) {
	        if (!this.projectId) return;
	        if (this.propertiesPage === 1) append = false;
	        this.loadingProperties = true;
	        if (this.propertiesPage === 1) {
	            this.countrySet.clear();
	            this.tagSet.clear();
	            this.unitTypeSet.clear();
	            this.availableCountries = [];
	            this.availableTags = [];
	            this.availableUnitTypes = [];
	        }
	        if (!append) {
	            this.properties = [];
	            this.filteredProperties = [];
	        }
	        const apiFilters = this.buildApiFilters();
	        this.propertiesService.getPropertiesAuth(apiFilters, { page: this.propertiesPage, perPage: 10 }).subscribe({
	            next: ({ items, total, page, perPage }) => {
                    const incoming = items.filter((p) => !p.isDeleted);
                    let nextProperties: Property[];
	                if (append) {
	                    const seen = new Set(this.properties.map((p) => p.id));
	                    nextProperties = [...this.properties, ...incoming.filter((p) => !seen.has(p.id))];
	                } else {
	                    nextProperties = incoming;
	                }
	                const safeTotal = Number.isFinite(total) ? total : (page - 1) * perPage + items.length;
	                const serverHasNext = safeTotal > page * perPage;
                    const hasNextPage = serverHasNext || (items.length >= perPage && nextProperties.length < safeTotal);
                    this.properties = nextProperties;
	                this.propertiesTotalCount = this.normalizeVisiblePropertyTotalCount(safeTotal, nextProperties.length, hasNextPage);
	                this.addFilterOptions(incoming);
	                this.propertiesHasNext = hasNextPage;
	                this.loadingProperties = false;
	                this.applyLocalFilters(this.properties);
	            },
	            error: () => {
	                if (!append) {
	                    this.properties = [];
	                    this.filteredProperties = [];
	                    this.propertiesTotalCount = 0;
	                    this.propertiesHasNext = false;
	                } else if (this.propertiesPage > 1) {
	                    this.propertiesPage -= 1;
	                }
	                this.loadingProperties = false;
	            }
	        });
	    }

	    loadPhases(append = false) {
	        if (!this.projectId) return;
	        if (this.phasesPage === 1) append = false;
	        this.loadingPhases = true;
	        this.projectsService.getPhasesByProjectAuth(this.projectId, { page: this.phasesPage, perPage: 10 }).subscribe({
	            next: (rows) => {
	                if (append) {
	                    const seen = new Set(this.phases.map((p) => p.id));
	                    this.phases = [...this.phases, ...rows.filter((p) => !seen.has(p.id))];
	                } else {
	                    this.phases = rows;
	                }
	                this.phasesHasNext = rows.length >= 10;
	                this.loadingPhases = false;
	            },
	            error: () => {
	                if (!append) {
	                    this.phases = [];
	                    this.phasesHasNext = false;
	                } else if (this.phasesPage > 1) {
	                    this.phasesPage -= 1;
	                }
	                this.loadingPhases = false;
	            }
	        });
	    }

    async onProjectImageSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.projectImageErrorMessage = null;
        this.projectImageError = false;

        if (!file) return;
        const validation = this.validateProjectImage(file);
        if (!validation.valid) {
            this.projectImageErrorMessage = validation.error || 'Invalid image';
            this.notifications.showError(this.projectImageErrorMessage).subscribe();
            return;
        }
        if (!this.projectId) return;

        this.uploadingProjectImage = true;
        this.projectImageErrorMessage = null;
        this.projectImageError = false;
        try {
            let updated: Project | null = null;
            try {
                updated = await firstValueFrom(this.projectsService.uploadProjectImage(this.projectId, file));
            } catch (err: any) {
                const status = err?.status ?? null;
                const code = err?.error?.error?.code || err?.error?.code || null;
                const message = err?.error?.error?.message || err?.error?.message || '';
                const looksLikeMissingRoute = status === 404 && code === 'NOT_FOUND' && String(message).trim() === 'Not found';
                if (!looksLikeMissingRoute) throw err;

                const uploaded = await firstValueFrom(
                    this.uploadService.uploadFile(file, { isPublic: true, owner: 'org', context: 'project', projectId: this.projectId })
                );
                const fileId = uploaded?.file_id ? String(uploaded.file_id) : '';
                if (!fileId) throw new Error('Upload failed');
                updated = await firstValueFrom(this.projectsService.updateProject(this.projectId, { cover_file_id: fileId }));
            }

            if (updated) {
                this.project = updated;
                this.refreshProjectImageUrl(updated);
            }
            this.notifications.showSuccess('Cover image updated').subscribe();
        } catch (err: any) {
            const message = err?.error?.message || err?.message || 'Failed to upload cover image';
            this.projectImageErrorMessage = message;
            this.projectImageError = true;
            this.notifications.showError(message).subscribe();
        } finally {
            this.uploadingProjectImage = false;
        }
    }

    onProjectImageError() {
        this.projectImageError = true;
    }

    private resolveProjectDocUrl(project: Project | null, type: 'brochure' | 'masterPlan' | 'locationMap'): string | null {
        if (!project) return null;
        const fileId = type === 'brochure' ? project.brochureFileId : type === 'masterPlan' ? project.masterPlanFileId : project.locationMapFileId;
        if (fileId) return this.uploadService.filePublicUrl(String(fileId));
        const direct = type === 'brochure' ? project.brochureUrl : type === 'masterPlan' ? project.masterPlanUrl : project.locationMapUrl;
        return direct ? String(direct) : null;
    }

    private resolveProjectOfferCoverUrl(project: Project | null): string | null {
        if (!project) return null;
        const direct = project.offerCoverUrl ? String(project.offerCoverUrl) : null;
        if (direct) return direct;
        const fileId = project.offerCoverFileId ? String(project.offerCoverFileId).trim() : '';
        return fileId ? this.uploadService.filePublicUrl(fileId) : null;
    }

    viewerQueryParams(url: string | null | undefined, title: string): Record<string, string> {
        const src = String(url || '').trim();
        if (!src) return { title };
        const fileId = this.extractFileIdFromStratusUrl(src);
        if (fileId) return { fileId, title };
        return { src, title };
    }

    openViewer(url: string | null | undefined, title: string, event?: Event): void {
        event?.stopPropagation();
        this.viewerDialog.openFromUrl(url, title, this.projectId || undefined);
    }

    filePublicUrl(fileId: string | null | undefined): string {
        const id = String(fileId || '').trim();
        return id ? this.uploadService.filePublicUrl(id) : '';
    }

    private extractFileIdFromStratusUrl(value: string): string | null {
        const raw = String(value || '').trim();
        if (!raw) return null;
        try {
            const url = new URL(raw);
            const path = url.pathname || '';
            const match =
                path.match(/\/files\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i)
                || path.match(/\/public\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$)/i);
            return match?.[1] ? String(match[1]) : null;
        } catch {
            return null;
        }
    }

    async onProjectDocSelected(event: Event, type: 'brochure' | 'masterPlan' | 'locationMap' | 'offerCover') {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;
        input.value = '';
        this.projectDocsError = null;
        if (!file || !this.projectId) return;

        this.uploadingProjectDocs = true;
        try {
            const uploaded = await firstValueFrom(
                this.uploadService.uploadFile(file, { isPublic: true, owner: 'org', context: 'project', projectId: this.projectId })
            );
            const fileId = uploaded?.file_id ? String(uploaded.file_id) : '';
            if (!fileId) throw new Error('Upload failed');

            const payload = type === 'brochure'
                ? { brochure_file_id: fileId }
                : type === 'masterPlan'
                    ? { master_plan_file_id: fileId }
                    : type === 'locationMap'
                        ? { location_map_file_id: fileId }
                        : { offer_cover_file_id: fileId };

            await firstValueFrom(this.projectsService.updateProject(this.projectId, payload));

            if (this.project) {
                if (type === 'brochure') {
                    this.project.brochureFileId = fileId;
                    this.project.brochureUrl = null;
                    this.projectBrochureUrl = this.resolveProjectDocUrl(this.project, 'brochure');
                } else {
                    if (type === 'masterPlan') {
                        this.project.masterPlanFileId = fileId;
                        this.project.masterPlanUrl = null;
                        this.projectMasterPlanUrl = this.resolveProjectDocUrl(this.project, 'masterPlan');
                    } else if (type === 'locationMap') {
                        this.project.locationMapFileId = fileId;
                        this.project.locationMapUrl = null;
                        this.projectLocationMapUrl = this.resolveProjectDocUrl(this.project, 'locationMap');
                    } else {
                        this.project.offerCoverFileId = fileId;
                        this.project.offerCoverUrl = null;
                        this.projectOfferCoverUrl = this.resolveProjectOfferCoverUrl(this.project);
                    }
                }
            } else {
                this.projectBrochureUrl = type === 'brochure' ? this.uploadService.filePublicUrl(fileId) : this.projectBrochureUrl;
                this.projectMasterPlanUrl = type === 'masterPlan' ? this.uploadService.filePublicUrl(fileId) : this.projectMasterPlanUrl;
                this.projectLocationMapUrl = type === 'locationMap' ? this.uploadService.filePublicUrl(fileId) : this.projectLocationMapUrl;
                this.projectOfferCoverUrl = type === 'offerCover' ? this.uploadService.filePublicUrl(fileId) : this.projectOfferCoverUrl;
            }

            this.notifications
                .showSuccess(
                    type === 'brochure'
                        ? 'Brochure updated'
                        : type === 'masterPlan'
                            ? 'Master plan updated'
                            : type === 'locationMap'
                                ? 'Location map updated'
                                : 'Offer cover updated'
                )
                .subscribe();
        } catch (err: any) {
            const message = err?.error?.message || err?.message || 'Failed to upload document';
            this.projectDocsError = message;
            this.notifications.showError(message).subscribe();
        } finally {
            this.uploadingProjectDocs = false;
        }
    }

    async clearProjectDoc(type: 'brochure' | 'masterPlan' | 'locationMap' | 'offerCover') {
        if (!this.projectId || this.uploadingProjectDocs) return;
        this.projectDocsError = null;
        this.uploadingProjectDocs = true;
        try {
            const payload = type === 'brochure'
                ? { brochure_file_id: null }
                : type === 'masterPlan'
                    ? { master_plan_file_id: null }
                    : type === 'locationMap'
                        ? { location_map_file_id: null }
                        : { offer_cover_file_id: null };

            await firstValueFrom(this.projectsService.updateProject(this.projectId, payload));

            if (this.project) {
                if (type === 'brochure') {
                    this.project.brochureFileId = null;
                    this.project.brochureUrl = null;
                    this.projectBrochureUrl = null;
                } else {
                    if (type === 'masterPlan') {
                        this.project.masterPlanFileId = null;
                        this.project.masterPlanUrl = null;
                        this.projectMasterPlanUrl = null;
                    } else if (type === 'locationMap') {
                        this.project.locationMapFileId = null;
                        this.project.locationMapUrl = null;
                        this.projectLocationMapUrl = null;
                    } else {
                        this.project.offerCoverFileId = null;
                        this.project.offerCoverUrl = null;
                        this.projectOfferCoverUrl = null;
                    }
                }
            }

            this.notifications.showSuccess('Document removed').subscribe();
        } catch (err: any) {
            const message = err?.error?.message || err?.message || 'Failed to remove document';
            this.projectDocsError = message;
            this.notifications.showError(message).subscribe();
        } finally {
            this.uploadingProjectDocs = false;
        }
    }

			    toggleEdit() {
			        this.editMode = !this.editMode;
			        this.projectErrorMessage = null;

                    if (this.editMode) {
                        setTimeout(() => {
                            const node = this.projectEditForm?.nativeElement;
                            if (!node) return;
                            node.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 0);
                        return;
                    }

				        if (!this.editMode && this.project) {
				            this.editName = this.project.name;
				            this.editDescription = this.project.description || '';
                            this.syncDevelopmentBadgeFromProject(this.project);
                            this.projectReservationsEnabled = Boolean(this.project.reservationsEnabled);
                            this.projectInvestorRequestOffersEnabled = Boolean(this.project.investorRequestOffersEnabled);
                            this.projectPartnerRequestOffersEnabled = Boolean(this.project.partnerRequestOffersEnabled);
                            this.projectCountry = this.project.countryCode || this.project.country || '';
			            this.projectLocationAddress = this.project.locationAddress || '';
			            this.editProjectLocationLink = this.project.locationLink || '';
		        }
		    }

	    saveProject() {
	        if (!this.projectId) return;
	        const name = (this.editName || '').trim();
	        if (!name) {
	            this.projectErrorMessage = 'Project name is required';
	            return;
	        }
	        this.projectErrorMessage = null;
	        this.savingProject = true;

	        const payload: Record<string, unknown> = { name };
	        if (this.editDescription && this.editDescription.trim()) {
	            payload['description'] = this.editDescription.trim();
	        }

		        const locationAddress = (this.projectLocationAddress || '').trim() || null;
		        payload['location_address'] = locationAddress;
		        const locationLink = (this.editProjectLocationLink || '').trim() || null;
		        if (locationLink && !/^https?:\/\//i.test(locationLink)) {
		            this.projectErrorMessage = 'Location link must start with http(s)://';
		            this.savingProject = false;
		            return;
		        }
		        payload['location_link'] = locationLink;
		        const country = (this.projectCountry || '').trim();
		        if (!country) {
		            this.projectErrorMessage = 'Country is required';
		            this.savingProject = false;
		            return;
		        }
		        payload['country'] = country.toUpperCase();

	        const badgeMode = String(this.developmentBadgeMode || '').trim();
	        const badgePresetMap: Record<string, string> = {
	            funding: 'Funding',
	            new_launch: 'New launch',
	            development_off_plan: 'New Development/Off-Plan',
	            off_plan: 'Off-plan',
	            under_construction: 'Under construction'
	        };
	        const badgeValueRaw = badgeMode === 'custom'
	            ? String(this.developmentBadgeCustom || '').trim()
	            : (badgePresetMap[badgeMode] || badgePresetMap['off_plan']);
	        if (!badgeValueRaw) {
	            this.projectErrorMessage = 'Please provide a custom development badge label.';
	            this.savingProject = false;
	            return;
	        }
	        if (badgeValueRaw.length > 60) {
	            this.projectErrorMessage = 'Development badge must be 60 characters or less.';
	            this.savingProject = false;
	            return;
		        }
            if (isFundingDevelopmentBadge(this.project?.developmentBadge) && !isFundingDevelopmentBadge(badgeValueRaw)
                && this.phases.some(phase => !phase.deliveryDate)) {
                this.projectErrorMessage = 'Delivery date is required for all phases before changing the Funding development badge.';
                this.savingProject = false;
                return;
            }
		        payload['development_badge'] = badgeValueRaw;
                if (!this.isAdminContext && this.reservationsFeatureAvailable) {
                    payload['reservations_enabled'] = this.projectReservationsEnabled;
                }
                payload['investor_request_offers_enabled'] = this.projectInvestorRequestOffersEnabled;
                payload['partner_request_offers_enabled'] = this.projectPartnerRequestOffersEnabled;

	        this.projectsService.updateProject(this.projectId, payload).subscribe({
		            next: () => {
		                this.savingProject = false;
	                this.editMode = false;
	                this.loadProject();
	            },
	            error: (err) => {
	                this.savingProject = false;
	                this.projectErrorMessage = err?.message || 'Failed to save project';
	            }
	        });
	    }

	    private syncDevelopmentBadgeFromProject(project: Project | null): void {
	        const raw = project?.developmentBadge ?? null;
	        const label = raw === null || raw === undefined ? '' : String(raw).trim();
	        if (!label) {
	            this.developmentBadgeMode = 'off_plan';
	            this.developmentBadgeCustom = '';
	            return;
	        }
	        const lower = label.toLowerCase().replace(/\\s+/g, ' ').trim();
	        const condensed = lower.replace(/[^a-z0-9]+/g, '');
            if (label === 'Funding') {
                this.developmentBadgeMode = 'funding';
                this.developmentBadgeCustom = '';
                return;
            }
	        if (condensed === 'newlaunch') {
	            this.developmentBadgeMode = 'new_launch';
	            this.developmentBadgeCustom = '';
	            return;
	        }
	        if (condensed === 'developmentoffplan') {
	            this.developmentBadgeMode = 'development_off_plan';
	            this.developmentBadgeCustom = '';
	            return;
	        }
	        if (condensed === 'offplan') {
	            this.developmentBadgeMode = 'off_plan';
	            this.developmentBadgeCustom = '';
	            return;
	        }
	        if (condensed === 'underconstruction') {
	            this.developmentBadgeMode = 'under_construction';
	            this.developmentBadgeCustom = '';
	            return;
	        }
	        this.developmentBadgeMode = 'custom';
	        this.developmentBadgeCustom = label;
	    }

    async deleteProject() {
        if (!this.projectId || this.deletingProject) return;
        const warning = 'Delete this project? All linked properties will be marked deleted and removed from public view.';
        const ok = await this.confirm.confirm({
            title: 'Delete project?',
            message: warning,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;
        this.deletingProject = true;
        this.projectsService.deleteProject(this.projectId).subscribe({
            next: async () => {
                try {
                    // Best-effort cascade: mark linked properties deleted on the frontend side if backend hasn't done it.
                    const props = (await this.propertiesService.getPropertiesByProject(this.projectId).toPromise()) || [];
                    const deletions = props
                        .filter((p: any) => p?.id)
                        .map((p: any) => this.propertiesService.softDeleteProperty(p.id).toPromise());
                    await Promise.allSettled(deletions);
                } catch {
                    /* best effort only */
                }
                this.deletingProject = false;
                this.notifications.showSuccess('Project deleted; linked properties have been removed from public view.').subscribe();
                this.navigateToProjectsHome();
            },
            error: (err) => {
                this.deletingProject = false;
                this.notifications.showError(err?.message || 'Failed to delete project').subscribe();
            }
        });
    }

    toggleVerified(): void {
        if (!this.projectId || !this.project || this.verifying) return;
        const next = !(this.project.isVerified === true);
        this.verifying = true;
        this.projectsService.updateProject(this.projectId, { is_verified: next }).subscribe({
            next: () => {
                this.project = { ...(this.project as Project), isVerified: next };
                this.verifying = false;
                this.notifications.showSuccess(next ? 'Project marked as verified' : 'Project verification removed').subscribe();
            },
            error: (err) => {
                this.verifying = false;
                this.notifications.showError(err?.message || 'Failed to update verification').subscribe();
            }
        });
    }

	    approveProject(): void {
	        if (!this.projectId || !this.project || this.approvalUpdating) return;
	        const status = String(this.project.status || '').trim().toLowerCase();
	        // Verify only — publishing to the public API is a separate Super Admin action.
	        const payload: any = { is_approved: true, is_verified: true };
        if (status === 'pendingapproval' || status === 'pending_approval') {
            payload.status = 'Active';
        }
        this.approvalUpdating = true;
        this.projectsService.updateProject(this.projectId, payload).subscribe({
            next: (updated) => {
                this.project = updated;
                this.approvalUpdating = false;
                this.notifications.showSuccess('Project approved').subscribe();
            },
            error: (err) => {
                this.approvalUpdating = false;
                this.notifications.showError(err?.message || 'Failed to approve project').subscribe();
            }
	        });
	    }

	    async approveProjectWithProperties(): Promise<void> {
	        if (!this.projectId || !this.project || this.approvalWithPropertiesUpdating) return;
	        const ok = await this.confirm.confirm({
	            title: 'Verify project + properties?',
	            message: 'This marks the project and all its non-deleted properties as nibni-verified. It does NOT make them publicly visible — publishing to the public API is a separate step.',
	            confirmText: 'Verify all',
	            cancelText: 'Cancel',
	            tone: 'default'
	        });
	        if (!ok) return;

	        this.approvalWithPropertiesUpdating = true;
	        const status = String(this.project.status || '').trim().toLowerCase();
	        // Verify only — publishing to the public API is a separate Super Admin action.
	        const payload: any = { is_approved: true, is_verified: true };
	        if (status === 'pendingapproval' || status === 'pending_approval') {
	            payload.status = 'Active';
	        }
	        const needsProjectUpdate = !(this.project.isApproved && this.project.isVerified) || Boolean(payload.status);

	        const approveProps = (updatedProject: Project | null) => {
	            this.projectsService.approveProjectProperties(this.projectId).subscribe({
	                next: () => {
	                    this.approvalWithPropertiesUpdating = false;
		                    if (updatedProject) this.project = updatedProject;
		                    this.notifications.showSuccess('Project and properties approved').subscribe();
		                    this.loadProject();
		                    this.reloadProperties();
		                },
	                error: (err) => {
	                    this.approvalWithPropertiesUpdating = false;
	                    this.notifications.showError(err?.message || 'Failed to approve project properties').subscribe();
	                }
	            });
	        };

	        if (!needsProjectUpdate) {
	            approveProps(this.project);
	            return;
	        }

	        this.projectsService.updateProject(this.projectId, payload).subscribe({
	            next: (updated) => approveProps(updated),
	            error: (err) => {
	                this.approvalWithPropertiesUpdating = false;
	                this.notifications.showError(err?.message || 'Failed to approve project').subscribe();
	            }
	        });
	    }

	    async moveProjectToDraft(): Promise<void> {
	        if (!this.projectId || !this.project || this.approvalUpdating) return;
	        const ok = await this.confirm.confirm({
	            title: 'Move project to Draft?',
            message: 'This will unpublish the project and move it back to Draft.',
            confirmText: 'Continue',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;

        this.approvalUpdating = true;
        this.projectsService.updateProject(this.projectId, { status: 'Draft', is_approved: false, is_public: false }).subscribe({
            next: (updated) => {
	                this.project = updated;
	                this.approvalUpdating = false;
	                this.notifications.showSuccess('Project unpublished').subscribe();
	                this.reloadProperties();
	            },
            error: (err) => {
                this.approvalUpdating = false;
                this.notifications.showError(err?.message || 'Failed to update project').subscribe();
            }
        });
    }

    async draftAllProperties(): Promise<void> {
        if (!this.projectId || this.draftingProperties) return;
        const ok = await this.confirm.confirm({
            title: 'Draft all properties?',
            message: 'This will move all non-deleted properties under this project to Draft and unpublish them.',
            confirmText: 'Continue',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;

        this.draftingProperties = true;
        this.projectsService.draftProjectProperties(this.projectId).subscribe({
            next: () => {
	                this.draftingProperties = false;
	                this.notifications.showSuccess('All project properties unpublished').subscribe();
	                this.reloadProperties();
	            },
            error: (err) => {
                this.draftingProperties = false;
                this.notifications.showError(err?.message || 'Failed to draft project properties').subscribe();
            }
        });
    }

    private navigateToProjectsHome(): void {
        const target = this.isAdminContext ? ['/admin/projects'] : ['/developer/projects'];
        this.router.navigate(target);
    }

    startCreatePhase() {
        this.phaseFormVisible = true;
        this.editingPhaseId = null;
        this.phaseName = '';
        this.phaseDeliveryDate = '';
        this.phaseStatus = 'Active';
        this.phaseError = null;
    }

    beginPhaseEdit(phase: Phase) {
        this.phaseFormVisible = true;
        this.editingPhaseId = phase.id;
        this.phaseName = phase.name;
        this.phaseDeliveryDate = phase.deliveryDate || '';
        this.phaseStatus = phase.status || 'Active';
        this.phaseError = null;
    }

    cancelPhaseEdit() {
        this.phaseFormVisible = false;
        this.editingPhaseId = null;
        this.phaseName = '';
        this.phaseDeliveryDate = '';
        this.phaseStatus = 'Active';
        this.phaseError = null;
    }

    get phaseDeliveryDateOptional(): boolean {
        return isFundingDevelopmentBadge(this.project?.developmentBadge);
    }

    savePhase() {
        if (!this.projectId) return;
        const name = (this.phaseName || '').trim();
        if (!name) {
            this.phaseError = 'Phase name is required';
            return;
        }
        const deliveryDate = String(this.phaseDeliveryDate || '').trim();
        if (!deliveryDate && !this.phaseDeliveryDateOptional) {
            this.phaseError = 'Delivery date is required';
            return;
        }
        const status = String(this.phaseStatus || '').trim() || 'Active';
        this.phaseError = null;
        this.phaseSaving = true;

        if (this.editingPhaseId) {
            this.phaseSavingId = this.editingPhaseId;
            this.projectsService.updatePhase(this.editingPhaseId, { name, deliveryDate, status }).subscribe({
                next: () => {
	                    this.phaseSaving = false;
	                    this.phaseSavingId = null;
	                    this.cancelPhaseEdit();
	                    this.reloadPhases();
	                },
                error: (err) => {
                    this.phaseSaving = false;
                    this.phaseSavingId = null;
                    this.phaseError = err?.message || 'Failed to update phase';
                }
            });
            return;
        }

        this.projectsService.createPhase(this.projectId, { name, deliveryDate, status }).subscribe({
	            next: () => {
	                this.phaseSaving = false;
	                this.cancelPhaseEdit();
	                this.reloadPhases();
	            },
            error: (err) => {
                this.phaseSaving = false;
                this.phaseError = err?.message || 'Failed to create phase';
            }
        });
    }

    async deletePhase(id: string) {
        if (!id) return;
        const ok = await this.confirm.confirm({
            title: 'Delete phase?',
            message: 'Delete this phase?',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;
        this.phaseSavingId = id;
        this.projectsService.deletePhase(id).subscribe({
	            next: () => {
	                this.phaseSavingId = null;
	                this.reloadPhases();
	            },
            error: (err) => {
                this.phaseSavingId = null;
                this.phaseError = err?.message || 'Failed to delete phase';
            }
        });
    }

    private refreshProjectImageUrl(project?: Project | null) {
        const id = project?.id || this.projectId;
        if (!id) {
            this.projectImageUrl = null;
            return;
        }
        const coverFileId = project?.coverFileId ? String(project.coverFileId).trim() : '';
        this.projectImageUrl = (project?.image as string) || (coverFileId ? this.uploadService.filePublicUrl(coverFileId) : null) || this.buildProjectImageUrl(id);
    }

    private buildProjectImageUrl(projectId: string): string {
        // vNext: project cover images must come from backend file URLs (Files service).
        // Fallback to a bundled placeholder so the UI never depends on Stratus bucket hostnames.
        return 'assets/images/placeholders/placeholder.svg';
    }

    private validateProjectImage(file: File): { valid: boolean; error?: string } {
        if (!file.type.startsWith('image/')) {
            return { valid: false, error: 'Please upload an image file.' };
        }
        return { valid: true };
    }

    descriptionParagraphs(text?: string | null): string[] {
        if (!text) return [];
        return text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
    }

    statusLabel(status?: string | null): string {
        if (!status) return 'Active';
        return status.toString().replace(/_/g, ' ');
    }

    // Draft/Unpublished are internal workflow states; only the super admin
    // (who runs the publishing console) needs to see them on the detail page.
    isSuperAdmin = false;
    showProjectStatusBadge(project: any): boolean {
        if (this.isSuperAdmin) return true;
        const key = String(project?.status || '').trim().toLowerCase();
        return key !== 'draft' && key !== 'unpublished';
    }

    private applyLocalFilters(source: Property[]) {
        const { minPrice, maxPrice, bedrooms, bathrooms, location, status, country, unitType, featureGarden, featureTerrace, featureGroundFloor, search, sortBy, tags } = this.filters;
        const searchTerm = (search || '').toLowerCase();
        this.filteredProperties = this.filterAndSort(source, { minPrice, maxPrice, bedrooms, bathrooms, location, status, country, unitType, featureGarden, featureTerrace, featureGroundFloor, searchTerm, sortBy, tags });
    }

    private filterAndSort(source: Property[], params: {
        minPrice?: number | null;
        maxPrice?: number | null;
        bedrooms?: number | null;
        bathrooms?: number | null;
        location?: string | null;
        status: string;
        country: string;
        unitType: string;
        featureGarden: boolean;
        featureTerrace: boolean;
        featureGroundFloor: boolean;
        searchTerm: string;
        sortBy: SortKey;
        tags: string[];
    }): Property[] {
        const { minPrice, maxPrice, bedrooms, bathrooms, location, status, country, unitType, featureGarden, featureTerrace, featureGroundFloor, searchTerm, sortBy, tags } = params;

        return source
            .filter(p => {
                const priceValue = this.convertPrice(p) ?? 0;
                const matchesMin = minPrice ? priceValue >= minPrice : true;
                const matchesMax = maxPrice ? priceValue <= maxPrice : true;
                const matchesBedrooms = bedrooms ? (p.bedrooms || 0) >= bedrooms : true;
                const matchesBathrooms = bathrooms ? (p.bathrooms || 0) >= bathrooms : true;
                const matchesLocation = location ? (p.location || '').toLowerCase().includes(location.toLowerCase()) : true;
                const matchesStatus = status === 'any'
                    ? true
                    : propertyStatusKey(p.statusBadge || p.status || '') === status.toLowerCase();
                const matchesCountry = country === 'any'
                    ? true
                    : ((p.countryCode || p.country || '').toString().toUpperCase()) === country.toUpperCase();
                const matchesSearch = searchTerm
                    ? [
                        p.unitNumber,
                        p.unitNumber ? `Unit ${p.unitNumber}` : null,
                        p.title,
                        p.tagLine,
                        p.description,
                        p.projectName,
                        p.phaseName,
                        p.location,
                        p.country,
                        ...this.propertyFeatureSearchTerms(p)
                    ].some(value => (value || '').toString().toLowerCase().includes(searchTerm))
                    : true;
                const matchesTags = tags.length === 0
                    ? true
                    : this.propertyFeatureLabels(p).some(tag => tags.some(t => t.toLowerCase() === tag.toLowerCase()));

                const matchesUnitType = unitType === 'any'
                    ? true
                    : (p.type || '').toString().toLowerCase() === unitType.toLowerCase();
                const matchesGarden = featureGarden ? propertyHasGarden(p) : true;
                const matchesTerrace = featureTerrace ? propertyHasTerrace(p) : true;
                const matchesGroundFloor = featureGroundFloor ? this.isGroundFloor(p.floor) : true;

                return matchesMin && matchesMax && matchesBedrooms && matchesBathrooms && matchesLocation && matchesStatus && matchesCountry && matchesSearch && matchesTags && matchesUnitType && matchesGarden && matchesTerrace && matchesGroundFloor;
            })
	            .sort((a, b) => {
	                switch (sortBy) {
	                    case 'availability': {
	                        const ra = this.availabilityRank(a);
	                        const rb = this.availabilityRank(b);
	                        if (ra !== rb) return ra - rb;
	                        return this.parseDate(b.updated_at || b.created_at) - this.parseDate(a.updated_at || a.created_at);
	                    }
	                    case 'price-low':
	                        return (this.convertPrice(a) || Infinity) - (this.convertPrice(b) || Infinity);
	                    case 'price-high':
	                        return (this.convertPrice(b) || -Infinity) - (this.convertPrice(a) || -Infinity);
                    case 'ppsqm-low':
                        return (this.pricePerSqm(a) || Infinity) - (this.pricePerSqm(b) || Infinity);
                    case 'ppsqm-high':
                        return (this.pricePerSqm(b) || -Infinity) - (this.pricePerSqm(a) || -Infinity);
                    case 'bedrooms':
                        return (b.bedrooms || 0) - (a.bedrooms || 0);
                    case 'bathrooms':
                        return (b.bathrooms || 0) - (a.bathrooms || 0);
                    case 'unit-type':
                        return (a.type || '').toString().localeCompare((b.type || '').toString());
                    case 'title':
                        return a.title.localeCompare(b.title);
                    case 'recent':
                    default:
                        return this.parseDate(b.updated_at || b.created_at) - this.parseDate(a.updated_at || a.created_at);
                }
            });
    }

    private buildApiFilters() {
        const filter: any = { project_id: this.projectId };
        if (this.filters.status && this.filters.status !== 'any') filter.status = this.filters.status;
        if (this.filters.country && this.filters.country !== 'any') filter.country = this.filters.country;
        if (this.filters.unitType && this.filters.unitType !== 'any') filter.type = this.filters.unitType;
        if (this.filters.bedrooms) filter.bedrooms = this.filters.bedrooms;
        if (this.filters.bathrooms) filter.bathrooms = this.filters.bathrooms;
        if (this.filters.location) filter.location = this.filters.location;
        if (this.filters.search) {
            const raw = String(this.filters.search || '').trim();
            const normalized = raw.replace(/^unit\\s+/i, '').trim();
            filter.search = normalized || raw;
        }
        return filter;
    }

    private addFilterOptions(items: Property[]) {
        for (const p of items) {
            const country = (p.countryCode || p.country || '').toString().toUpperCase();
            if (country) this.countrySet.add(country);
            this.propertyFeatureLabels(p).forEach(tag => {
                if (tag) this.tagSet.add(tag);
            });
            const unitType = (p.type || '').toString().trim();
            if (unitType) this.unitTypeSet.add(unitType);
        }
        this.availableCountries = Array.from(this.countrySet).sort();
        this.availableTags = Array.from(this.tagSet).sort();
        this.availableUnitTypes = Array.from(this.unitTypeSet).sort();
    }

    private pricePerSqm(p: Property): number | null {
        const price = this.convertPrice(p);
        const area = p.bua || p.totalArea || p.area || null;
        if (!price || !area || area <= 0) return null;
        return price / area;
    }

    private isGroundFloor(floor: string | null | undefined): boolean {
        if (!floor) return false;
        const v = String(floor).trim().toLowerCase();
        if (v === '0' || v === '0.0') return true;
        if (v.includes('ground')) return true;
        if (v === 'gf' || v.startsWith('gf ')) return true;
        return false;
    }

    private parseDate(value?: string | null): number {
        if (!value) return 0;
        const normalized = value
            .replace(' ', 'T')
            .replace(/:(\d{3})$/, '.$1Z');
        const ts = Date.parse(normalized);
        return Number.isFinite(ts) ? ts : 0;
    }

    private availabilityRank(property: Property): number {
        const badge = propertyStatusKey(property.statusBadge || property.status);
        if (badge === 'available') return 0;
        if (badge === 'reserved' || badge === 'reservedwithdeposit') return 1;
        if (badge === 'contracted' || badge === 'sold') return 2;
        return 3;
    }

    private propertyFeatureLabels(property: Property): string[] {
        return propertyFeatureLabels(property);
    }

    private propertyFeatureSearchTerms(property: Property): string[] {
        return propertyFeatureSearchTerms(property);
    }

    private convertPrice(property: Property): number | null {
        if (property?.price === null || property?.price === undefined) return null;
        const fromCode = property.currency || property.currencyInfo?.code || 'USD';
        return this.currencyService.convert(property.price, fromCode);
    }

    isLongDescription(text?: string | null): boolean {
        if (!text) return false;
        return text.length > 320 || text.includes('\n\n');
    }

	    projectLocationLabel(project?: Project | null): string | null {
	        if (!project) return null;
	        const label = project?.locationAddress || project?.location || null;
	        const value = label ? String(label).trim() : '';
	        if (!value) return null;
	        if (/^[A-Z]{2}$/.test(value.toUpperCase())) return null;
	        return value;
	    }

    projectLocationLink(project: Project | null | undefined, locationLabel: string | null | undefined): string {
        const direct = String(project?.locationLink || '').trim();
        if (direct && /^https?:\/\//i.test(direct)) return direct;
        const fallback = String(locationLabel || '').trim() || this.projectCountryLabel(project) || String(project?.country || '').trim();
        return this.googleMapsSearchUrl(fallback || 'nibni');
    }

    projectCountryLabel(project?: Project | null): string | null {
        return this.countryNameFromCode(project?.country);
    }

    private googleMapsSearchUrl(query: string): string {
        const q = String(query || '').trim();
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q || 'nibni')}`;
    }

    private countryNameFromCode(value: string | null | undefined): string | null {
        if (!value) return null;
        const normalized = String(value).trim();
        if (!normalized) return null;
        const upper = normalized.toUpperCase();
        if (!/^[A-Z]{2}$/.test(upper)) return normalized;
        if (upper === 'EG') return 'Egypt';
        try {
            const display = new (Intl as any).DisplayNames(['en'], { type: 'region' });
            return display.of(upper) || upper;
        } catch {
            return upper;
        }
    }

    vendorInitial(name?: string | null): string {
        if (!name) return '?';
        return name.trim().charAt(0).toUpperCase();
    }

    onVendorAvatarError(): void {
        this.vendorLogoFailed = true;
    }

    private getErrorMessage(err: any, fallback: string): string {
        return err?.error?.message || err?.message || fallback;
    }

    private normalizeVisiblePropertyTotalCount(reportedTotal: number, loadedCount: number, hasNextPage: boolean): number {
        const safeLoaded = Math.max(0, Number(loadedCount) || 0);
        const safeReported = Number.isFinite(reportedTotal) ? Math.max(0, Number(reportedTotal)) : safeLoaded;
        if (hasNextPage) return Math.max(safeReported, safeLoaded);
        return safeLoaded || safeReported;
    }
}
